import {Component, effect, inject, input, signal} from '@angular/core';
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {LineChartModule} from "@swimlane/ngx-charts";
import {Location, NgClass, SlicePipe} from "@angular/common";
import {SpinnerComponent} from "src/app/presentation/common/spinner/spinner.component";
import {Animal, Paddock} from "src/app/infrastructure/moovement-api/models/asset";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {BluetoothSvgComponent, Status} from "src/app/presentation/common/icons/bluetooth-svg/bluetooth-svg.component";
import {PaddockComponent} from "src/app/presentation/main-view/information-center/animals/paddock/paddock.component";
import {concatMap, tap} from 'rxjs';
import {AggregatedDeviceStateStore} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.store';
import {DeviceApiClient} from 'src/app/infrastructure/moovement-api/web-api/device.api-client';
import {InterDeviceAssociationType} from 'src/app/domain/devices/inter-device-association';
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {unassignedAnimalsPaddock} from 'src/app/domain/assets/paddocks/unassigned-animals-paddock';
import {AggregatedDeviceStateService} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.service';
import {PaddockStore} from 'src/app/domain/assets/paddocks/paddock.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {Router} from '@angular/router';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {NetworkStatusStore} from 'src/app/infrastructure/network/network-status.store';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {AnimalSelectionStore} from 'src/app/domain/assets/animals/animal-selection.store';
import {AssetsStore} from 'src/app/domain/assets/assets.store';
import {DeviceAssociationService} from 'src/app/domain/devices/device-association.service';

@Component({
  selector: 'app-customize-bluetooth-proximity-sensor',
  imports: [
    GenericButtonComponent,
    LineChartModule,
    NgClass,
    BluetoothSvgComponent,
    SlicePipe,
    SpinnerComponent,
    PaddockComponent
],
  templateUrl: './customize-bluetooth-proximity-sensor.component.html',
  styleUrl: './customize-bluetooth-proximity-sensor.component.css'
})
export class CustomizeBluetoothProximitySensorComponent {

  id = input.required<string>();

  loading = signal(true);
  paddocks = signal<Paddock[] | undefined>(undefined);

  unassignedPaddock: Paddock = unassignedAnimalsPaddock;

  aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  paddockStore = inject(PaddockStore);

  Status = Status;

  animalsStore = inject(AnimalsStore);
  networkStatusStore = inject(NetworkStatusStore);

  private assignedDeviceIds = signal<Set<string>>(new Set());
  private allAnimalsSelected = false;

  private animalSelectionStore = inject(AnimalSelectionStore);
  private assetStore = inject(AssetsStore);
  private deviceAssociationStore = inject(DeviceAssociationStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private router = inject(Router);

  constructor(
    private aggregatedDeviceStateService: AggregatedDeviceStateService,
    private dataSubscriptionService: DataSubscriptionService,
    private deviceApiClient: DeviceApiClient,
    private deviceAssociationService: DeviceAssociationService,
    protected viewportService: ViewportService
  ) {
    effect(() => {
      if (this.paddockStore.loading()) return;

      const paddocks = this.paddockStore.entities();
      this.paddocks.set(paddocks);
    });

    effect(() => {
      const state = this.aggregatedDeviceStateStore.entityMap()[this.id()];
      if (!state || !state.associatedDevices) return;

      const assignedDeviceIds = state.associatedDevices.get(InterDeviceAssociationType.BLUETOOTH_SCANNER_ASSIGNMENT);

      this.assignedDeviceIds.set(new Set(assignedDeviceIds));
    });

    effect(() => {
      const assignedDeviceIds = this.assignedDeviceIds();
      const assignedAnimals = Array.from(assignedDeviceIds)
        .map(deviceId => this.animalsStore.deviceIdToAnimal().get(deviceId))
        .filter(device => device !== undefined);

      this.animalSelectionStore.setAllEntities(assignedAnimals);
    });

    effect(() => {
      this.loading.set(this.aggregatedDeviceStateStore.loading() || this.paddockStore.loading())
    })
  }

  back(): void {
    this.location.back();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }

  checkUncheckAllAnimals() {
    if (this.allAnimalsSelected) {
      this.animalSelectionStore.reset();
    } else {
      this.animalSelectionStore.setAllEntities([...this.animalsStore.entities(), ...this.animalsStore.unassignedAnimals()]);
    }
    this.allAnimalsSelected = !this.allAnimalsSelected;
  }

  applyChanges() {
    const targetDeviceIds = this.animalSelectionStore.entities()
      .map(animal => this.animalsStore.animalIdToDeviceId().get(animal.id))
      .filter(deviceId => deviceId !== undefined);

    const deviceId = this.id();
    this.deviceApiClient.updateInterDeviceAssociations(deviceId, targetDeviceIds, InterDeviceAssociationType.BLUETOOTH_SCANNER_ASSIGNMENT).pipe(
      concatMap(() => this.aggregatedDeviceStateService.reloadAggregatedDeviceState(deviceId)),
      tap(() => this.internalNotificationsStore.addNotification("Successfully updated scanner assignment.", InternalNotificationType.SUCCESS, false))
    ).subscribe();
  }

  removeAssociation() {
    const assetId = this.deviceAssociationStore.deviceIdToAssetId().get(this.id());
    if (!assetId) return;

    const asset = this.assetStore.assetsById().get(assetId);
    if (!asset) return;

    const deviceId = this.id();
    const slicedDeviceId = deviceId.slice(-5);
    if (!confirm(`Are you sure you want to remove scanning device '${slicedDeviceId}' from '${asset.name}'?`)) return;

    this.deviceApiClient.updateInterDeviceAssociations(deviceId, [], InterDeviceAssociationType.BLUETOOTH_SCANNER_ASSIGNMENT).pipe(
      concatMap(() => this.deviceAssociationService.dissociateDevice(deviceId)),
      concatMap(() => this.aggregatedDeviceStateService.reloadAggregatedDeviceState(deviceId)),
      tap(() => this.router.navigate(['/main/proximity-sample-points'])),
      tap(() => this.internalNotificationsStore.addNotification("Successfully removed scanner association.", InternalNotificationType.SUCCESS, false))
    ).subscribe();
  }

  getAnimalsFor(paddock: Paddock): Animal[] {
    return this.animalsStore.entities().filter(animal => animal.assignedPaddockId == paddock.id);
  }
}
